// ── Session Context Panel ────────────────────────────────────
// Cowork-style right panel showing session artifacts, uploaded files,
// executed actions, and thinking patterns in one unified view.

import React, { useState, useMemo } from 'react';

const FILE_ICONS = {
  '.pdf': '📄', '.docx': '📝', '.doc': '📝',
  '.xlsx': '📊', '.xls': '📊', '.csv': '📊',
  '.pptx': '📑', '.txt': '📃', '.md': '📃',
  '.json': '{ }', '.yaml': '⚙', '.yml': '⚙',
  '.html': '⟨/⟩', '.css': '⟨/⟩',
};

const ARTIFACT_ICONS = {
  export: '📦', deck: '📑', document: '📝', debate: '⚔',
  refine: '◎', prototype: '▣', experiment: '⚗', canvas: '◧',
  landscape: '🌄', timeline: '⏱', dashboard: '📊', mockup: '▣',
};

const FORMAT_BADGES = {
  pptx: 'PPTX', md: 'MD', docx: 'DOCX', html: 'HTML', pdf: 'PDF',
};

const STREAM_LABELS = {
  debate: { icon: '⚔', label: 'Debate' },
  refine: { icon: '◎', label: 'Refine' },
  portfolio: { icon: '◈', label: 'Portfolio' },
  experiment: { icon: '⚗', label: 'Experiment' },
  prototype: { icon: '▣', label: 'Prototype' },
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function Section({ title, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ctx-section">
      <button className="ctx-section-header" onClick={() => setOpen(v => !v)}>
        <span className="ctx-section-title">{title}{count > 0 && <span className="ctx-badge">{count}</span>}</span>
        <span className={`ctx-chevron ${open ? 'ctx-chevron--open' : ''}`}>›</span>
      </button>
      {open && <div className="ctx-section-body">{children}</div>}
    </div>
  );
}

export default function SessionContextPanel({
  isOpen,
  onClose,
  artifacts = [],
  canvasArtifacts = [],
  sessionFiles = [],
  debates = [],
  chatMessages = [],
  generationFlow,
  pipelineStages,
  patternExecState,
  debateStream,
  refineStream,
  portfolioStream,
  experimentStream,
  prototypeStream,
  mode,
  nodeCount = 0,
  refineRounds = 0,
}) {
  // ── Derive activity timeline from existing data ──
  const activityTimeline = useMemo(() => {
    const events = [];

    // From chat messages' executedActions
    chatMessages.forEach(msg => {
      if (msg.executedActions?.length) {
        msg.executedActions.forEach(action => {
          events.push({ type: 'action', label: action, timestamp: msg.timestamp });
        });
      }
    });

    // From debates
    debates.forEach((d, i) => {
      events.push({ type: 'debate', label: `Debate round ${i + 1}`, timestamp: d.timestamp || d.createdAt });
    });

    // From artifacts creation
    artifacts.forEach(a => {
      events.push({ type: 'artifact', label: a.title || a.type, timestamp: a.createdAt });
    });

    // From generation flow
    if (generationFlow?.length) {
      generationFlow.filter(s => s.status === 'done').forEach(s => {
        events.push({ type: 'generation', label: s.label, detail: s.nodeCount ? `${s.nodeCount} nodes` : null, timestamp: s.completedAt || s.startedAt });
      });
    }

    return events
      .filter(e => e.timestamp)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [chatMessages, debates, artifacts, generationFlow]);

  // ── Active streams ──
  const activeStreams = useMemo(() => {
    const streams = [];
    if (debateStream?.status && debateStream.status !== 'done' && debateStream.status !== 'error') {
      streams.push({ key: 'debate', ...STREAM_LABELS.debate, detail: debateStream.status });
    }
    if (refineStream?.status && refineStream.status !== 'done' && refineStream.status !== 'error') {
      streams.push({ key: 'refine', ...STREAM_LABELS.refine, detail: `Round ${refineStream.round || 1}/${refineStream.maxRounds || 3}` });
    }
    if (portfolioStream?.status && portfolioStream.status !== 'done' && portfolioStream.status !== 'error') {
      streams.push({ key: 'portfolio', ...STREAM_LABELS.portfolio, detail: portfolioStream.status });
    }
    if (experimentStream?.status && experimentStream.status !== 'done' && experimentStream.status !== 'error') {
      streams.push({ key: 'experiment', ...STREAM_LABELS.experiment, detail: experimentStream.status });
    }
    if (prototypeStream?.status && prototypeStream.status !== 'done' && prototypeStream.status !== 'error') {
      streams.push({ key: 'prototype', ...STREAM_LABELS.prototype, detail: prototypeStream.status });
    }
    return streams;
  }, [debateStream, refineStream, portfolioStream, experimentStream, prototypeStream]);

  // ── Pattern execution stages ──
  const patternStages = useMemo(() => {
    if (!patternExecState?.stages?.length) return [];
    return patternExecState.stages;
  }, [patternExecState]);

  if (!isOpen) return null;

  const allArtifacts = [
    ...artifacts.map(a => ({ ...a, source: 'artifact' })),
    ...canvasArtifacts.map(a => ({ ...a, source: 'canvas', createdAt: a.generatedAt })),
  ].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const hasProgress = activeStreams.length > 0 || activityTimeline.length > 0;
  const hasArtifacts = allArtifacts.length > 0;
  const hasContext = sessionFiles.length > 0;
  const hasPatterns = patternStages.length > 0 || debates.length > 0 || refineRounds > 0 || (pipelineStages?.length > 0);

  return (
    <div className="context-panel">
      <div className="ctx-header">
        <span className="ctx-header-title">Session Context</span>
        <button className="ctx-close" onClick={onClose} title="Close">×</button>
      </div>

      {/* Session summary bar */}
      <div className="ctx-summary-bar">
        <span className="ctx-summary-chip">{mode || 'idea'}</span>
        <span className="ctx-summary-chip">{nodeCount} nodes</span>
        {allArtifacts.length > 0 && <span className="ctx-summary-chip">{allArtifacts.length} artifacts</span>}
        {sessionFiles.length > 0 && <span className="ctx-summary-chip">{sessionFiles.length} files</span>}
      </div>

      <div className="ctx-scroll">
        {/* ── Progress Section ── */}
        <Section title="Progress" count={activeStreams.length + activityTimeline.length} defaultOpen={true}>
          {activeStreams.length > 0 && (
            <div className="ctx-live-group">
              {activeStreams.map(s => (
                <div key={s.key} className="ctx-item ctx-item--live">
                  <span className="ctx-live-dot" />
                  <span className="ctx-item-icon">{s.icon}</span>
                  <span className="ctx-item-label">{s.label}</span>
                  <span className="ctx-item-detail">{s.detail}</span>
                </div>
              ))}
            </div>
          )}
          {activityTimeline.length > 0 ? (
            activityTimeline.slice(0, 20).map((ev, i) => (
              <div key={i} className="ctx-item">
                <span className="ctx-item-icon">
                  {ev.type === 'action' ? '⚡' : ev.type === 'debate' ? '⚔' : ev.type === 'artifact' ? '📦' : '◉'}
                </span>
                <span className="ctx-item-label">{ev.label}</span>
                {ev.detail && <span className="ctx-item-detail">{ev.detail}</span>}
                <span className="ctx-item-time">{formatTime(ev.timestamp)}</span>
              </div>
            ))
          ) : activeStreams.length === 0 ? (
            <div className="ctx-empty">No activity yet</div>
          ) : null}
        </Section>

        {/* ── Artifacts Section ── */}
        <Section title="Artifacts" count={allArtifacts.length} defaultOpen={hasArtifacts}>
          {allArtifacts.length > 0 ? allArtifacts.map(a => (
            <div key={a.id} className="ctx-item ctx-item--artifact">
              <span className="ctx-item-icon">{ARTIFACT_ICONS[a.type] || '📦'}</span>
              <div className="ctx-item-content">
                <span className="ctx-item-label">{a.title || a.type}</span>
                {a.summary && <span className="ctx-item-summary">{a.summary}</span>}
              </div>
              {a.format && (
                <span className="ctx-format-badge">{FORMAT_BADGES[a.format] || a.format.toUpperCase()}</span>
              )}
              {a.source === 'canvas' && <span className="ctx-format-badge">CANVAS</span>}
              <span className="ctx-item-time">{formatTime(a.createdAt)}</span>
            </div>
          )) : (
            <div className="ctx-empty">No artifacts generated yet</div>
          )}
        </Section>

        {/* ── Context Section ── */}
        <Section title="Context" count={sessionFiles.length} defaultOpen={hasContext}>
          {sessionFiles.length > 0 ? sessionFiles.map(f => (
            <div key={f.id} className="ctx-item">
              <span className="ctx-item-icon">{FILE_ICONS[f.type] || '📄'}</span>
              <span className="ctx-item-label" title={f.name}>
                {f.name.length > 30 ? f.name.slice(0, 27) + '...' : f.name}
              </span>
              {f.size && <span className="ctx-item-detail">{f.size < 1024 ? `${f.size}B` : f.size < 1024 * 1024 ? `${Math.round(f.size / 1024)}KB` : `${(f.size / (1024 * 1024)).toFixed(1)}MB`}</span>}
            </div>
          )) : (
            <div className="ctx-empty">No files attached</div>
          )}
        </Section>

        {/* ── Thinking Patterns Section ── */}
        <Section title="Thinking Patterns" count={(patternStages.length > 0 ? 1 : 0) + debates.length + (refineRounds > 0 ? 1 : 0)} defaultOpen={hasPatterns}>
          {/* Active pattern execution */}
          {patternExecState?.patternName && (
            <div className="ctx-pattern-block">
              <div className="ctx-pattern-name">
                {patternExecState.complete ? '✓' : '◌'} {patternExecState.patternName}
              </div>
              {patternStages.map((stage, i) => (
                <div key={i} className={`ctx-stage ${stage.status === 'done' ? 'ctx-stage--done' : stage.status === 'running' ? 'ctx-stage--running' : ''}`}>
                  <span className="ctx-stage-dot">{stage.status === 'done' ? '●' : stage.status === 'running' ? '◐' : '○'}</span>
                  <span className="ctx-stage-label">{stage.label || stage.type || `Stage ${i + 1}`}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pipeline stages */}
          {pipelineStages?.length > 0 && (
            <div className="ctx-pattern-block">
              <div className="ctx-pattern-name">Pipeline</div>
              {pipelineStages.map((stage, i) => (
                <div key={i} className={`ctx-stage ${stage.status === 'done' ? 'ctx-stage--done' : stage.status === 'active' ? 'ctx-stage--running' : ''}`}>
                  <span className="ctx-stage-dot">{stage.status === 'done' ? '●' : stage.status === 'active' ? '◐' : '○'}</span>
                  <span className="ctx-stage-label">{stage.label || stage.type}</span>
                </div>
              ))}
            </div>
          )}

          {/* Debate rounds summary */}
          {debates.length > 0 && (
            <div className="ctx-item">
              <span className="ctx-item-icon">⚔</span>
              <span className="ctx-item-label">Debate</span>
              <span className="ctx-item-detail">{debates.length} round{debates.length !== 1 ? 's' : ''} completed</span>
            </div>
          )}

          {/* Refine rounds summary */}
          {refineRounds > 0 && (
            <div className="ctx-item">
              <span className="ctx-item-icon">◎</span>
              <span className="ctx-item-label">Refine</span>
              <span className="ctx-item-detail">{refineRounds} round{refineRounds !== 1 ? 's' : ''} completed</span>
            </div>
          )}

          {!hasPatterns && <div className="ctx-empty">No patterns executed yet</div>}
        </Section>
      </div>
    </div>
  );
}
